// WO-33 — admin controls for onboarding (spec §8): the one on/off switch (R1),
// the demo video link, and "Reset onboarding" for a test account (R5).
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ListChecks } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function readError(res) {
  const data = await res.json().catch(() => ({}));
  return typeof data.detail === 'string' ? data.detail : `The server answered ${res.status}.`;
}

export default function AdminOnboardingCard() {
  const { getToken } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [video, setVideo] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  }), [getToken]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/settings`, { headers: headers() });
      if (!res.ok) { setLoadError(await readError(res)); return; }
      const data = await res.json();
      setSettings(data);
      setVideo(data.demo_video_url || '');
      setLoadError(null);
    } catch (e) {
      setLoadError('Could not reach the server.');
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const put = async (patch, okMessage) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/settings`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(patch),
      });
      if (!res.ok) { toast.error(await readError(res)); return; }
      const data = await res.json();
      setSettings(data);
      setVideo(data.demo_video_url || '');
      toast.success(okMessage);
    } catch (e) {
      toast.error('Could not reach the server. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/admin/reset`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) { toast.error(await readError(res)); return; }
      const data = await res.json();
      toast.success(`Onboarding reset for ${data.email}. It shows at their next page load.`);
      setEmail('');
    } catch (e) {
      toast.error('Could not reach the server. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-0 bg-card/50" data-testid="admin-onboarding-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-primary" />
          Onboarding
        </CardTitle>
        <CardDescription>
          Welcome window, getting-started checklist, tour, help menu and helpful empty screens.
          Off hides all of it at once, with no redeploy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loadError && <p className="text-sm text-destructive" data-testid="admin-onboarding-error">{loadError}</p>}
        {settings && (
          <>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="onb-switch" className="font-semibold">Onboarding is {settings.onboarding_on ? 'ON' : 'OFF'}</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  On by default. Every account sees the welcome window and the checklist until it closes or hides them. Switching off hides everything at the next page change.
                </p>
              </div>
              <Switch
                id="onb-switch"
                checked={!!settings.onboarding_on}
                disabled={busy}
                onCheckedChange={(v) => put({ onboarding_on: v }, v ? 'Onboarding switched ON.' : 'Onboarding switched OFF.')}
                data-testid="admin-onboarding-switch"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="onb-video">Demo video link</Label>
              <div className="flex gap-2">
                <Input id="onb-video" placeholder="https://…" value={video} onChange={(e) => setVideo(e.target.value)} data-testid="admin-onboarding-video" />
                <Button variant="outline" disabled={busy || video === (settings.demo_video_url || '')}
                  onClick={() => put({ demo_video_url: video }, video ? 'Demo video link saved.' : 'Demo video link removed.')}
                  data-testid="admin-onboarding-video-save">
                  Save
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Empty = "Watch the demo" stays hidden in the help menu.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onb-reset">Reset onboarding for a test account</Label>
              <div className="flex gap-2">
                <Input id="onb-reset" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-onboarding-reset-email" />
                <Button variant="outline" disabled={busy || !email.trim()} onClick={reset} data-testid="admin-onboarding-reset">
                  Reset
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">The account sees the welcome window and the checklist again, as a new account would.</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
